import { useState, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLang } from '../../../hooks/useLang';
import { useScrollProgress } from '../../../hooks/useScrollProgress';
import InstitutionCompare from './InstitutionCompare';

const hkInstitutions = [
  {
    rank: 'S', name: 'HashKey Exchange', license: 'VATP 1类 & 7类', type: '持牌交易所',
    rwa: 'STO上市交易、RWA二级市场、机构托管',
    url: 'https://hashkey.com', email: 'compliance@hashkey.com',
    sandbox: '是', tvl: '头部',
    note: '香港首批持牌VATP（牌照编号：AAB）。2023年6月获批，提供BTC/ETH现货交易及STO代币二级市场，是香港RWA发行首选平台。创始人：肖风博士（万向区块链创始人，RWA理论推动者）。关联机构：HashKey Capital（VC）、HashKey Custody（托管）、HashKey Chain（公链L2）。',
    color: '#7C3AED',
    founded: '2018',
    bigEvents: '香港首批VATP、与HKMA Project Ensemble合作、HashKey Chain L2上线（2024.09）',
    rwaCase: '承销香港SFC首批RWA代币、为Bosera代币化基金提供托管服务',
  },
  {
    rank: 'S', name: 'OSL Exchange', license: 'VATP 1类 & 7类', type: '持牌交易所',
    rwa: 'RWA代币发行、交易、机构托管一体化',
    url: 'https://osl.com', email: 'osl@osl.com',
    sandbox: '是', tvl: '头部',
    note: '上市公司BC Technology（863 HK）旗下持牌平台。2020年获香港首批VASP牌照，是亚洲历史最长的持牌数字资产平台，合规标杆。首席执行官：Patrick Pan。',
    color: '#7C3AED',
    founded: '2018',
    bigEvents: '全球首批获SFC双牌照VATP、OSL Custody获首批机构托管资质',
    rwaCase: '为多家机构代币化产品提供交易和托管服务、支持HSBC Orion债券代币结算',
  },
  {
    rank: 'A', name: 'HSBC Hong Kong / 汇丰香港', license: '持牌银行（AMLO授权）', type: '国际投行',
    rwa: 'HSBC Orion债券代币化平台、黄金代币HSBC Gold Token',
    url: 'https://www.business.hsbc.com.hk', email: 'hk.rwa@hsbc.com',
    sandbox: '是（Project Ensemble）', tvl: '千亿级',
    note: '全球资产规模最大银行之一。2023年推出Orion代币化平台，完成HK$8亿绿色债券代币化，是首只由中央银行以数字形式发行的政府绿色债券承销行。亚太区数字资产业务负责人：John O\'Neill。',
    color: '#FB923C',
    founded: '1865',
    bigEvents: '全球首只数字化政府绿债承销（HKMA HK$8亿）、HSBC Gold Token黄金代币化',
    rwaCase: 'HSBC Orion：为摩根大通、汇丰自身发行代币化结构性产品',
  },
  {
    rank: 'A', name: 'Standard Chartered HK / 渣打香港', license: '持牌银行（AMLO授权）', type: '国际投行',
    rwa: 'Libeara美债代币化平台、代币化存款试点',
    url: 'https://sc.com/hk', email: 'hk.digital@sc.com',
    sandbox: '是（Project Ensemble）', tvl: '千亿级',
    note: '渣打旗下数字资产子公司Zodia Custody已在多国获持牌托管资质。2023年推出Libeara平台，代币化美国国债供香港专业投资者认购，是传统外资银行最积极布局RWA的标杆之一。',
    color: '#FB923C',
    founded: '1853',
    bigEvents: 'Libeara美债代币化平台发布、Zodia Custody全球托管扩展',
    rwaCase: 'Libeara Fund：代币化美国国债，面向香港专业投资者认购',
  },
  {
    rank: 'A', name: 'BOCI 中银国际', license: 'SFC持牌法团（1/4/9类）', type: '投资银行',
    rwa: '数字化结构性票据（2.28亿港元），以太坊链上发行',
    url: 'https://bocigroup.com', email: 'ir@bocigroup.com',
    sandbox: '否', tvl: '百亿级',
    note: '中国银行全资子公司，中资背景首家完成以太坊STO的投行。2023年与瑞银（UBS）合作，在以太坊公链发行2.28亿港元（含等值人民币及欧元）数字化结构性票据，是中资机构RWA里程碑事件。',
    color: '#34D399',
    founded: '1979',
    bigEvents: '2023年以太坊STO（2.28亿港元）、与UBS联合发行',
    rwaCase: '首只由中资机构在公链发行的结构性票据',
  },
  {
    rank: 'A', name: 'Securitize (Hong Kong)', license: 'SFC第1/4/9类', type: '代币化平台',
    rwa: 'STO全流程技术+合规平台，合作KKR、BlackRock、Hamilton Lane',
    url: 'https://securitize.io', email: 'asia@securitize.io',
    sandbox: '是', tvl: '10亿+美元',
    note: '全球最大RWA代币化技术服务商，总部美国并在香港持牌。为BlackRock BUIDL基金（$10亿+）提供代币化发行服务，持有美国SEC注册经纪交易商资质+香港SFC 1/4/9类牌照。CEO：Carlos Domingo。',
    color: '#60A5FA',
    founded: '2017',
    bigEvents: 'BlackRock BUIDL基金代币化（$10亿+）、KKR私募基金代币化、Hamilton Lane代币化',
    rwaCase: 'BlackRock BUIDL（美债）、KKR（私募股权）、Hamilton Lane（私募信贷）代币化',
  },
  {
    rank: 'A', name: 'DBS Hong Kong / 星展香港', license: '持牌银行（AMLO授权）', type: '商业银行',
    rwa: '数字债券、代币化存款、机构RWA全系列',
    url: 'https://www.dbs.com.hk', email: 'hk.digital@dbs.com',
    sandbox: '是（Project Ensemble）', tvl: '千亿级',
    note: '新加坡最大银行，亚太区RWA布局最全面的商业银行。旗下DDEx（DBS Digital Exchange）持有MAS牌照提供代币化债券交易，与香港多项RWA试点合作。DBS Bank已发行多批代币化绿色债券。',
    color: '#34D399',
    founded: '1968',
    bigEvents: 'DBS Digital Exchange上线、代币化绿债发行、Project Ensemble核心参与方',
    rwaCase: 'DBS DDEx：代币化新加坡政府债券、绿色债券交易平台',
  },
  {
    rank: 'A', name: 'Republic Asia', license: 'SFC第1类', type: '代币化平台',
    rwa: '私募基金/房地产STO，聚焦亚洲市场',
    url: 'https://republic.com', email: 'asia@republic.com',
    sandbox: '否', tvl: '中型',
    note: '美国Republic集团（管理资产$30亿+）亚洲分支，专注面向亚太机构和合格投资者的私募RWA发行。已在美国协助100+私募项目代币化发行，香港分支2023年获SFC 1类牌照，专注亚太市场扩展。',
    color: '#A78BFA',
    founded: '2016',
    bigEvents: 'Republic Note代币发行、香港SFC持牌、亚太私募RWA布局',
    rwaCase: '房地产基金代币化、私募股权二级市场代币化',
  },
  {
    rank: 'A', name: 'HKEX（香港交易所）', license: 'SFC认可交易所', type: '证券交易所',
    rwa: 'FINI新股数字化平台、探索DSE数字证券交易',
    url: 'https://www.hkex.com.hk', email: 'communications@hkex.com',
    sandbox: '是', tvl: '万亿级',
    note: '全球最大交易所集团之一（市值超港股总值40%）。2023年上线FINI平台将IPO结算从T+5缩短至T+2，是香港代币化证券基础设施建设的核心推动者。CEO：Nicolas Aguzin。正在研究允许RWA代币在DSE（数字证券交易所）上市。',
    color: '#F59E0B',
    founded: '1891',
    bigEvents: 'FINI平台（T+2结算）上线、DSE概念发布、CBDC结算试验参与',
    rwaCase: 'FINI平台已完成200+IPO代币化结算；探索DSE接受RWA代币上市',
  },
  {
    rank: 'A', name: 'Bosera Asset Management / 博时基金（香港）', license: 'SFC第9类（资管）', type: '资产管理',
    rwa: '代币化货币市场基金，与HashKey合作',
    url: 'https://www.bosera.com.hk', email: 'hk@bosera.com',
    sandbox: '是', tvl: '百亿级',
    note: '内地顶级公募基金博时基金旗下香港子公司，是首批与VATP合作探索代币化基金份额的内地机构。2024年与HashKey合作推出代币化货币市场基金（MMF），面向香港专业投资者，是内地公募机构进军RWA的里程碑。',
    color: '#C084FC',
    founded: '1998（内地），2008（香港）',
    bigEvents: '2024年与HashKey联合发行代币化MMF（香港首只）',
    rwaCase: '博时HashKey货币市场基金代币化产品',
  },
  {
    rank: 'A', name: 'Harvest Global Investments / 嘉实国际', license: 'SFC第1/4/9类', type: '资产管理',
    rwa: '探索ETF代币化、RWA产品，服务内地机构',
    url: 'https://www.harvestglobal.com.hk', email: 'hk@harvestglobal.com.hk',
    sandbox: '否', tvl: '百亿级',
    note: '内地大型基金公司嘉实基金旗下香港子公司（管理规模$100亿+）。持有SFC 1/4/9类完整牌照，是内地机构通过香港布局RWA的重要通道之一。',
    color: '#38BDF8',
    founded: '2008',
    bigEvents: '内地机构首批完整SFC三类牌照持有人之一、探索ETF代币化方案',
    rwaCase: '探索将内地ETF份额在香港代币化，面向外资机构',
  },
  {
    rank: 'B', name: 'Mox Bank', license: '虚拟银行牌照（HKMA）', type: '虚拟银行',
    rwa: 'RWA零售产品探索中（未上线）',
    url: 'https://mox.com', email: 'support@mox.com',
    sandbox: '否', tvl: '小型',
    note: '渣打集团联合携程、PCCW共同持股的香港虚拟银行，专注面向零售客户的数字金融服务。目前RWA业务仍处于研究阶段，尚未向客户提供相关产品，是未来值得关注的零售RWA入口。',
    color: '#94A3B8',
    founded: '2020',
    bigEvents: 'HKMA虚拟银行牌照（8家之一）、零售数字银行服务',
    rwaCase: '暂无上线RWA产品，探索中',
  },
];

const domesticProviders = [
  {
    name: '蚂蚁链（Ant Chain）', group: '阿里巴巴/蚂蚁集团', beian: '2019-S1-001',
    type: '联盟链+BaaS', focus: '供应链金融、司法存证、票据、公益', iot: '支持',
    cert: '已备案+国密', note: '国内最大联盟链，200+合作伙伴，亿级TPS技术能力，已累计处理10亿+链上存证，是蚂蚁集团数字经济战略核心资产',
    url: 'https://antchain.antgroup.com',
    cases: '支付宝公益透明链、浙江法院区块链证据平台、粮食溯源、供应链金融',
  },
  {
    name: '腾讯云区块链（TBaaS）', group: '腾讯', beian: '2019-S1-003',
    type: 'BaaS平台（多链兼容）', focus: '政务、金融、医疗、文化', iot: '支持',
    cert: '已备案+国密', note: '兼容Hyperledger Fabric、FISCO BCOS多链架构，腾讯云生态整合，支持微信生态数据存证，累计服务500+企业客户',
    url: 'https://cloud.tencent.com/product/tbaas',
    cases: '国家版权局存证平台、广州市政务服务区块链、腾讯存证云',
  },
  {
    name: 'FISCO BCOS / 微众银行', group: '微众银行（腾讯持股）', beian: '2019-S1-002',
    type: '开源联盟链底层', focus: '金融机构联盟链合规底层框架', iot: '部分',
    cert: '已备案+国密+等保三级', note: '开源金融级联盟链，被400+银行和机构采用，国密全面支持，TPS达20000+，是国内金融联盟链事实标准',
    url: 'https://fisco-bcos.org',
    cases: '深圳市民服务中心、珠三角金融联盟链、多地应收账款登记平台',
  },
  {
    name: '长安链（ChainMaker）', group: '北京微芯研究院（国家级）', beian: '2021-G1-008',
    type: '国家级开源联盟链', focus: '政务、国企、金融合规场景', iot: '支持',
    cert: '已备案+国密+商密资质', note: '国家重点项目，中央网信办、工信部等支持，多省政务采用，是目前政府背书最强的联盟链平台，TPS达10万+',
    url: 'https://chainmaker.org.cn',
    cases: '河南政务区块链平台、国家电网碳排放监测、农业部溯源系统',
  },
  {
    name: '万向区块链（Wanxiang Blockchain）', group: '万向集团', beian: '2019-S1-023',
    type: '联盟链+企业区块链服务', focus: 'RWA、供应链金融、能源、农业', iot: '支持',
    cert: '已备案+国密', note: '万向集团旗下，HashKey Exchange创始人肖风博士联合创立，是中国最早系统性推动RWA概念的机构，连续举办全球区块链峰会（GBAF），2024年管理区块链项目投资超50亿人民币',
    url: 'https://www.wanxiang.io',
    cases: '上海临港RWA试验区参与方、区块链+供应链金融平台、新能源积分上链',
  },
  {
    name: 'Conflux（树图网络）', group: '上海树图区块链研究院', beian: '2022-S1-045',
    type: '合规公链（特殊）', focus: '跨境RWA、Web3、数字文旅', iot: '部分',
    cert: '国家许可+已备案', note: '中国唯一获得政府支持的合规公链（注：严格意义上为"特许合规公链"）。由清华大学姚期智院士团队研究成果转化，获上海市政府支持，与香港金管局、内地证监会合作探索跨境RWA场景。TPS 3000-6000',
    url: 'https://confluxnetwork.org',
    cases: '上海数字人民币NFT文旅项目、香港与内地跨境RWA试验、数字人民币集成',
  },
  {
    name: 'HashKey Chain（公链L2）', group: 'HashKey Group', beian: '海外合规部署',
    type: '以太坊L2公链（EVM兼容）', focus: '香港持牌RWA代币发行、合规DeFi', iot: '支持',
    cert: 'HashKey Exchange SFC持牌母公司保障', note: '基于以太坊OP Stack构建的以太坊L2公链，由HashKey Group于2024年9月上线。专为香港合规RWA而设计：内置KYC模块、合规DApp环境，是目前唯一由SFC持牌机构运营的RWA专用公链L2',
    url: 'https://hashkey.com/chain',
    cases: 'HashKey平台RWA代币二级市场、香港合规STO代币发行、Bosera代币化基金结算',
  },
  {
    name: 'PlatON（矩阵元）', group: '万向区块链关联', beian: '2021-S1-056',
    type: '隐私计算+区块链', focus: '隐私数据RWA、金融数据共享', iot: '支持',
    cert: '已备案', note: '专注隐私计算与区块链结合，采用MPC多方安全计算技术，解决RWA发行中的数据隐私问题（如投资者身份数据共享不暴露明文）。与金融机构合作数据共享联盟。',
    url: 'https://platon.network',
    cases: '银行间数据共享联盟、医疗数据隐私计算、企业征信链上化',
  },
  {
    name: '百度超级链（XuperChain）', group: '百度', beian: '2019-S1-005',
    type: '开源公/联盟链', focus: '内容版权、溯源、政务存证', iot: '支持',
    cert: '已备案', note: '并行DAG架构，高吞吐量，百度AI+区块链深度融合，版权保护场景占据国内主导地位，已累计存储版权数据10亿+条',
    url: 'https://xuper.baidu.com',
    cases: '百度内容平台版权存证、文化和旅游部文物溯源、百度网盘存证功能',
  },
];

const licenseSystem = [
  {
    id: '1', name: '第1类牌照', fullName: '证券交易', icon: 'ri-stock-line', color: '#7C3AED',
    scope: '买卖上市证券（股票、债券、代币化证券）', rwaRelevance: '最核心',
    desc: 'RWA代币发行中最基础牌照。所有STO/RWA代币的分销、承销、撮合交易均须1类持牌机构参与。SFC持牌VATP（如HashKey、OSL）通过1类牌照提供代币化证券交易服务。',
    canDo: ['RWA代币分销', '证券型代币买卖撮合', '二级市场交易', '自营做市'],
  },
  {
    id: '2', name: '第2类牌照', fullName: '期货合约交易', icon: 'ri-line-chart-line', color: '#3B82F6',
    scope: '买卖期货合约（商品期货、金融期货）', rwaRelevance: '较低',
    desc: '与RWA直接关联度较低，但大宗商品RWA（如黄金、石油）若涉及期货对冲，需要参与方持有2类牌照。',
    canDo: ['大宗商品RWA对冲', '期货合约撮合', '衍生品RWA定价'],
  },
  {
    id: '3', name: '第3类牌照', fullName: '外汇交易', icon: 'ri-exchange-dollar-line', color: '#10B981',
    scope: '外汇买卖', rwaRelevance: '跨境结算',
    desc: '跨境RWA资金回流需经外汇结算，若涉及境外资金换汇则需3类持牌机构参与。人民币/港元/美元之间的结算处理。',
    canDo: ['RWA跨境资金换汇', '外汇对冲安排', '多币种RWA结算'],
  },
  {
    id: '4', name: '第4类牌照', fullName: '就证券提供意见', icon: 'ri-customer-service-line', color: '#F59E0B',
    scope: '向客户提供证券投资建议', rwaRelevance: '高度相关',
    desc: '为RWA提供投资建议（如项目评级、推荐认购）须持4类牌照。RWA项目招募说明书须由4类持牌机构审核，向专业投资者推介产品亦须持牌。',
    canDo: ['RWA投资建议', '项目尽职调查', '招募说明书审核', '向PI推介产品'],
  },
  {
    id: '5', name: '第5类牌照', fullName: '就期货合约提供意见', icon: 'ri-briefcase-line', color: '#8B5CF6',
    scope: '期货合约投资建议', rwaRelevance: '较低',
    desc: '与RWA直接关联较低，大宗商品RWA若含期货对冲策略建议则需持牌。',
    canDo: ['大宗商品RWA期货策略', '套期保值建议'],
  },
  {
    id: '6', name: '第6类牌照', fullName: '就机构融资提供意见', icon: 'ri-building-line', color: '#EF4444',
    scope: '企业融资、IPO、并购等资本市场顾问', rwaRelevance: '重要',
    desc: '企业通过RWA进行股权/债权融资时，负责提供架构设计、估值、投资者引导等服务的机构须持6类牌照。类似传统IPO保荐人角色。',
    canDo: ['RWA发行架构设计', '企业RWA融资顾问', '估值报告', '资本市场策略'],
  },
  {
    id: '7', name: '第7类牌照', fullName: '提供自动化交易服务', icon: 'ri-robot-line', color: '#0EA5E9',
    scope: '运营自动化交易系统（ATS）/ 电子证券交易平台', rwaRelevance: '最核心',
    desc: 'VATP的第7类牌照允许运营证券代币交易平台（相当于数字资产交易所牌照）。HashKey和OSL均持有7类+1类双牌照，才能合法运营RWA代币交易所。',
    canDo: ['运营代币化证券交易所', 'RWA代币撮合匹配', 'ATS系统运营', '做市商系统'],
  },
  {
    id: '8', name: '第8类牌照', fullName: '证券保证金融资', icon: 'ri-money-cny-circle-line', color: '#D97706',
    scope: '向客户提供融资用于购买证券', rwaRelevance: '未来场景',
    desc: '当RWA代币二级市场成熟后，投资者可能需要用代币抵押融资。目前RWA领域尚无持8类+VATP组合的机构，是未来金融业务的发展方向。',
    canDo: ['RWA代币抵押融资', '杠杆认购RWA', '代币化证券回购'],
  },
  {
    id: '9', name: '第9类牌照', fullName: '资产管理', icon: 'ri-pie-chart-line', color: '#6366F1',
    scope: '受托管理证券/期货/代币化资产组合', rwaRelevance: '最核心',
    desc: '管理RWA基金（代表投资者投资）、为RWA投资组合进行再平衡、运营代币化货币市场基金均须9类持牌。与1类牌照并列为RWA最核心的两类监管牌照。',
    canDo: ['RWA基金管理', '代币化资产组合管理', 'NAV计算与披露', '代币化货币市场基金'],
  },
  {
    id: 'VATP', name: 'VATP附加条件', fullName: '虚拟资产交易平台', icon: 'ri-shield-keyhole-line', color: '#DC2626',
    scope: '专属虚拟资产/代币的交易、托管、清算服务', rwaRelevance: '核心',
    desc: '2023年新制度下，持有1类+7类牌照并满足SFC附加条件（VATP要求）才能运营虚拟资产交易平台。VATP要求包括：冷/热钱包分离、100%用户资产储备、AML合规、保险安排等额外要求。',
    canDo: ['虚拟资产交易所运营', 'RWA代币一级发行', '代币托管（含自托管要求）', '用户资产隔离保管'],
  },
];

const vaspVvatpDiff = {
  vasp: {
    title: 'VASP',
    full: 'Virtual Asset Service Provider（虚拟资产服务提供商）',
    scope: '国际通用概念（FATF定义），泛指提供虚拟资产相关服务的机构',
    hk: '香港早期监管框架使用VASP概念（2020-2022年），泛指持牌数字资产机构',
    global: '全球通用，FATF"旅行规则"要求VASP间传递交易信息',
    who: '交易所、钱包服务商、OTC机构、代币发行人',
    regulate: 'FATF标准，各国自行落地（如美国FinCEN、新加坡MAS使用VASP）',
    color: '#3B82F6',
  },
  vatp: {
    title: 'VATP',
    full: 'Virtual Asset Trading Platform（虚拟资产交易平台）',
    scope: '香港专用监管词汇（SFC定义），特指经SFC批准的虚拟资产交易平台',
    hk: '2023年《虚拟资产服务提供商条例》正式生效后，香港改用VATP作为交易平台专属标识，须持1类+7类牌照并满足附加条件',
    global: '香港特有称谓，其他司法管辖区不使用此词',
    who: '目前仅12家香港SFC批准的平台可使用VATP称谓',
    regulate: 'SFC全面监管：冷热钱包比例、100%储备证明、保险安排、KYC/AML',
    color: '#7C3AED',
  },
};

const selfCheckList = [
  'SFC 20条合规自查清单（RWA发行方必读）',
  '所在地监管许可：资产权属是否已完成法律确权？',
  'SPV架构：是否已设立离岸SPV（开曼/BVI）且具备实质运营？',
  '持牌承销：是否已对接SFC持牌1类/9类机构？',
  '独立估值：是否委托Big4或认可机构出具资产评估报告？',
  '真实出售：律师是否出具"真实出售意见书"确认破产隔离？',
  '投资者限制：是否仅向香港专业投资者（PI≥800万港元）发售？',
  'KYC/AML：是否已建立完整的投资者身份核查流程（保存7年）？',
  '白名单：是否已部署链上合规白名单智能合约？',
  'FATF旅行规则：是否使用Notabene等工具合规处理转账信息传递？',
  '审计：是否已委托第三方对智能合约进行安全审计（CertiK等）？',
  '双重托管：是否实现底层资产+代币私钥双独立托管安排？',
  '披露：是否已准备合规的招股说明书（含四大风险模块）？',
  '退出机制：赎回流程、流动性安排、清算程序是否写入合约？',
  '资金回流：是否通过CIPS/QFLP等合规渠道安排资金回流？',
  '投后披露：是否建立季度NAV更新和年度审计报告机制？',
];

// ─── i18n label maps ───────────────────────────────────────────────────────────
type LangKey = 'zh' | 'en' | 'fr' | 'es' | 'pt' | 'ja' | 'de' | 'ko' | 'ar' | 'ru';

const LABELS: Record<string, Record<LangKey, string>> = {
  sectionBadge: { zh: '香港持牌机构与牌照体系全解', en: 'HK Licensed Institutions & License System', fr: 'Institutions Agréées HK & Système de Licences', es: 'Instituciones Licenciadas HK & Sistema de Licencias', pt: 'Instituições Licenciadas HK & Sistema de Licenças', ja: '香港認可機関・ライセンス体系全解', de: 'Vollständige Karte des Hongkong RWA-Ökosystems', ko: '홍콩 인가기관 및 라이선스 체계 전해', ar: 'المؤسسات المرخصة في HK ونظام التراخيص', ru: 'Лицензированные учреждения HK и система лицензий' },
  sectionTitle: { zh: '香港 RWA 生态机构全图谱', en: 'Hong Kong RWA Ecosystem Full Map', fr: 'Carte Complète de l\'Écosystème RWA de Hong Kong', es: 'Mapa Completo del Ecosistema RWA de Hong Kong', pt: 'Mapa Completo do Ecossistema RWA de Hong Kong', ja: '香港RWAエコシステム全体マップ', de: 'Vollständige Karte des Hongkong RWA-Ökosystems', ko: '홍콩 RWA 생태계 전체 지도', ar: 'خريطة كاملة لنظام RWA البيئي في هونغ كونغ', ru: 'Полная карта экосистемы RWA Гонконга' },
  sectionDesc: { zh: '12家持牌VATP · 国内领先服务商 · 香港金融牌照体系全解 · VASP vs VATP区别深析 · SFC合规自查清单', en: '12 Licensed VATPs · Domestic Leaders · HK License System · VASP vs VATP Analysis · SFC Compliance Checklist', fr: '12 VATP Agréés · Leaders Nationaux · Système de Licences HK · Analyse VASP vs VATP · Liste de Conformité SFC', es: '12 VATP Licenciados · Líderes Nacionales · Sistema de Licencias HK · Análisis VASP vs VATP · Lista de Cumplimiento SFC', pt: '12 VATPs Licenciados · Líderes Nacionais · Sistema de Licenças HK · Análise VASP vs VATP · Lista de Conformidade SFC', ja: '12認可VATP・国内リーダー・HKライセンス体系・VASP vs VATP分析・SFCコンプライアンスチェックリスト', de: '12 Lizenzierte VATPs · Nationale Marktführer · HK-Lizenzsystem · VASP vs VATP Analyse · SFC-Compliance-Checkliste', ko: '12개 인가 VATP · 국내 선도업체 · HK 라이선스 체계 · VASP vs VATP 분석 · SFC 컴플라이언스 체크리스트', ar: '12 VATP مرخصاً · قادة محليون · نظام تراخيص HK · تحليل VASP مقابل VATP · قائمة امتثال SFC', ru: '12 лицензированных VATP · Национальные лидеры · Система лицензий HK · Анализ VASP vs VATP · Контрольный список SFC' },
  tabHK: { zh: '🇭🇰 香港12家VATP', en: '🇭🇰 12 HK VATPs', fr: '🇭🇰 12 VATP HK', es: '🇭🇰 12 VATP HK', pt: '🇭🇰 12 VATPs HK', ja: '🇭🇰 香港12 VATP', de: '🇭🇰 12 HK VATPs', ko: '🇭🇰 홍콩 12 VATP', ar: '🇭🇰 12 VATP في HK', ru: '🇭🇰 12 VATP Гонконга' },
  tabDomestic: { zh: '🇨🇳 国内服务商', en: '🇨🇳 Domestic Providers', fr: '🇨🇳 Prestataires Nationaux', es: '🇨🇳 Proveedores Nacionales', pt: '🇨🇳 Fornecedores Nacionais', ja: '🇨🇳 国内業者', de: '🇨🇳 Nationale Anbieter', ko: '🇨🇳 국내 업체', ar: '🇨🇳 مزودون محليون', ru: '🇨🇳 Национальные провайдеры' },
  tabLicense: { zh: '📋 香港牌照体系全解', en: '📋 HK License System', fr: '📋 Système de Licences HK', es: '📋 Sistema de Licencias HK', pt: '📋 Sistema de Licenças HK', ja: '📋 HKライセンス体系全解', de: '📋 HK-Lizenzsystem', ko: '📋 HK 라이선스 체계 전해', ar: '📋 نظام تراخيص HK', ru: '📋 Система лицензий HK' },
  tabVasp: { zh: '⚖️ VASP vs VATP', en: '⚖️ VASP vs VATP', fr: '⚖️ VASP vs VATP', es: '⚖️ VASP vs VATP', pt: '⚖️ VASP vs VATP', ja: '⚖️ VASP vs VATP', de: '⚖️ VASP vs VATP', ko: '⚖️ VASP vs VATP', ar: '⚖️ VASP مقابل VATP', ru: '⚖️ VASP vs VATP' },
  tabChecklist: { zh: '✅ 合规自查清单', en: '✅ Compliance Checklist', fr: '✅ Liste de Conformité', es: '✅ Lista de Cumplimiento', pt: '✅ Lista de Conformidade', ja: '✅ コンプライアンスチェックリスト', de: '✅ Compliance-Checkliste', ko: '✅ 컴플라이언스 체크리스트', ar: '✅ قائمة الامتثال', ru: '✅ Контрольный список' },
  sRankLabel: { zh: 'S 级推荐', en: 'S-Class Recommended', fr: 'Recommandé Classe S', es: 'Recomendado Clase S', pt: 'Recomendado Classe S', ja: 'Sクラス推奨', de: 'S-Klasse Empfohlen', ko: 'S급 추천', ar: 'موصى به من الفئة S', ru: 'Рекомендуется класс S' },
  sRankDesc: { zh: '持牌VATP，RWA发行首选平台', en: 'Licensed VATP, preferred RWA issuance platform', fr: 'VATP agréé, plateforme d\'émission RWA préférée', es: 'VATP licenciado, plataforma de emisión RWA preferida', pt: 'VATP licenciado, plataforma de emissão RWA preferida', ja: '認可VATP、RWA発行優先プラットフォーム', de: 'Lizenziertes VATP, bevorzugte RWA-Ausgabeplattform', ko: '인가 VATP, RWA 발행 우선 플랫폼', ar: 'VATP مرخص، منصة إصدار RWA المفضلة', ru: 'Лицензированный VATP, предпочтительная платформа выпуска RWA' },
  aRankLabel: { zh: 'A 级推荐', en: 'A-Class Recommended', fr: 'Recommandé Classe A', es: 'Recomendado Clase A', pt: 'Recomendado Classe A', ja: 'Aクラス推奨', de: 'A-Klasse Empfohlen', ko: 'A급 추천', ar: 'موصى به من الفئة A', ru: 'Рекомендуется класс A' },
  aRankDesc: { zh: '持牌或持牌银行，具备RWA业务能力', en: 'Licensed or licensed bank with RWA capabilities', fr: 'Agréé ou banque agréée avec capacités RWA', es: 'Licenciado o banco licenciado con capacidades RWA', pt: 'Licenciado ou banco licenciado com capacidades RWA', ja: '認可または認可銀行、RWA業務能力あり', de: 'Lizenziert oder lizenzierte Bank mit RWA-Fähigkeiten', ko: '인가 또는 인가 은행, RWA 업무 능력 보유', ar: 'مرخص أو بنك مرخص بقدرات RWA', ru: 'Лицензированный или лицензированный банк с возможностями RWA' },
  bRankLabel: { zh: 'B 级观察', en: 'B-Class Watch', fr: 'Observation Classe B', es: 'Observación Clase B', pt: 'Observação Classe B', ja: 'Bクラス観察', de: 'B-Klasse Beobachtung', ko: 'B급 관찰', ar: 'مراقبة الفئة B', ru: 'Наблюдение класс B' },
  bRankDesc: { zh: '已持牌，RWA业务探索中', en: 'Licensed, exploring RWA business', fr: 'Agréé, exploration des activités RWA', es: 'Licenciado, explorando negocios RWA', pt: 'Licenciado, explorando negócios RWA', ja: '認可済み、RWA業務を探索中', de: 'Lizenziert, RWA-Geschäft wird erkundet', ko: '인가 완료, RWA 사업 탐색 중', ar: 'مرخص، يستكشف أعمال RWA', ru: 'Лицензирован, исследует бизнес RWA' },
  dataSource: { zh: '数据来源：SFC官网（www.sfc.hk）持牌机构名单，更新至2026年3月。实际业务合作前请以官网最新信息为准。', en: 'Data source: SFC official website (www.sfc.hk) licensed institution list, updated to March 2026. Please verify with the official website before actual business cooperation.', fr: 'Source des données : liste des institutions agréées du site officiel SFC (www.sfc.hk), mise à jour en mars 2026. Veuillez vérifier sur le site officiel avant toute coopération commerciale.', es: 'Fuente de datos: lista de instituciones licenciadas del sitio web oficial de SFC (www.sfc.hk), actualizada a marzo de 2026. Verifique con el sitio web oficial antes de cualquier cooperación comercial.', pt: 'Fonte de dados: lista de instituições licenciadas do site oficial da SFC (www.sfc.hk), atualizada em março de 2026. Verifique no site oficial antes de qualquer cooperação comercial.', ja: 'データソース：SFC公式サイト（www.sfc.hk）認可機関リスト、2026年3月更新。実際の業務協力前に公式サイトで確認してください。', de: 'Datenquelle: SFC-offizielle Website (www.sfc.hk) Liste lizenzierter Institutionen, aktualisiert bis März 2026. Bitte vor der tatsächlichen Geschäftskooperation auf der offiziellen Website überprüfen.', ko: '데이터 출처: SFC 공식 웹사이트(www.sfc.hk) 인가기관 목록, 2026년 3월 업데이트. 실제 사업 협력 전 공식 웹사이트에서 확인하세요.', ar: 'مصدر البيانات: قائمة المؤسسات المرخصة على الموقع الرسمي لـ SFC (www.sfc.hk)، محدثة حتى مارس 2026. يرجى التحقق من الموقع الرسمي قبل أي تعاون تجاري فعلي.', ru: 'Источник данных: список лицензированных учреждений на официальном сайте SFC (www.sfc.hk), обновлен до марта 2026 г. Пожалуйста, проверьте на официальном сайте перед фактическим деловым сотрудничеством.' },
  domesticWarning: { zh: '重要提示：国内区块链服务商提供联盟链技术服务，严禁发行加密货币或虚拟货币。Conflux为特殊政府许可情形，须在合规指引下使用。所有业务须在网信办备案范围内开展，不得绕过监管发行任何类型代币。', en: 'Important: Domestic blockchain service providers offer consortium chain technology services. Issuing cryptocurrencies or virtual currencies is strictly prohibited. Conflux is a special government-permitted case and must be used under compliance guidelines. All business must be conducted within the CAC registration scope.', fr: 'Important : Les prestataires de services blockchain nationaux offrent des services de technologie de chaîne de consortium. L\'émission de crypto-monnaies ou de monnaies virtuelles est strictement interdite. Conflux est un cas spécial autorisé par le gouvernement et doit être utilisé selon les directives de conformité.', es: 'Importante: Los proveedores de servicios blockchain nacionales ofrecen servicios de tecnología de cadena de consorcio. La emisión de criptomonedas o monedas virtuales está estrictamente prohibida. Conflux es un caso especial permitido por el gobierno y debe usarse bajo pautas de cumplimiento.', pt: 'Importante: Os provedores de serviços blockchain nacionais oferecem serviços de tecnologia de cadeia de consórcio. A emissão de criptomoedas ou moedas virtuais é estritamente proibida. Conflux é um caso especial permitido pelo governo e deve ser usado sob diretrizes de conformidade.', ja: '重要：国内ブロックチェーンサービスプロバイダーはコンソーシアムチェーン技術サービスを提供します。暗号通貨や仮想通貨の発行は厳禁です。Confluxは特別な政府許可ケースであり、コンプライアンスガイドラインに従って使用する必要があります。', de: 'Wichtig: Inländische Blockchain-Dienstleister bieten Konsortiumsketten-Technologiedienste an. Die Ausgabe von Kryptowährungen oder virtuellen Währungen ist streng verboten. Conflux ist ein speziell staatlich genehmigter Fall und muss gemäß Compliance-Richtlinien verwendet werden.', ko: '중요: 국내 블록체인 서비스 제공업체는 컨소시엄 체인 기술 서비스를 제공합니다. 암호화폐 또는 가상화폐 발행은 엄격히 금지됩니다. Conflux는 특별 정부 허가 사례이며 컴플라이언스 지침에 따라 사용해야 합니다.', ar: 'مهم: يقدم مزودو خدمات البلوكتشين المحليون خدمات تقنية سلسلة الاتحاد. يُحظر تمامًا إصدار العملات المشفرة أو الافتراضية. Conflux حالة مرخصة حكوميًا خاصة ويجب استخدامها وفق إرشادات الامتثال.', ru: 'Важно: Отечественные поставщики услуг блокчейн предлагают технологические услуги консорциумных цепочек. Выпуск криптовалют или виртуальных валют строго запрещен. Conflux — особый случай с государственным разрешением, должен использоваться в соответствии с руководящими принципами соответствия.' },
  licenseSystemTitle: { zh: '香港SFC金融牌照体系完整说明', en: 'Complete HK SFC Financial License System', fr: 'Système Complet de Licences Financières SFC de Hong Kong', es: 'Sistema Completo de Licencias Financieras SFC de Hong Kong', pt: 'Sistema Completo de Licenças Financeiras SFC de Hong Kong', ja: '香港SFC金融ライセンス体系完全説明', de: 'Vollständiges HK SFC Finanzlizenzsystem', ko: '홍콩 SFC 금융 라이선스 체계 완전 설명', ar: 'نظام تراخيص SFC المالية الكامل في هونغ كونغ', ru: 'Полная система финансовых лицензий SFC Гонконга' },
  licenseSystemDesc: { zh: '第1-9类受规管活动牌照 + VATP虚拟资产交易平台附加条件 | 以下逐类说明每张牌照对RWA的意义', en: 'Type 1-9 Regulated Activity Licenses + VATP Additional Conditions | Each license\'s significance for RWA explained below', fr: 'Licences d\'activités réglementées de type 1 à 9 + conditions supplémentaires VATP | Signification de chaque licence pour RWA expliquée ci-dessous', es: 'Licencias de actividades reguladas tipo 1-9 + condiciones adicionales VATP | Significado de cada licencia para RWA explicado a continuación', pt: 'Licenças de atividades reguladas tipo 1-9 + condições adicionais VATP | Significado de cada licença para RWA explicado abaixo', ja: '第1-9類規制活動ライセンス + VATP追加条件 | 各ライセンスのRWAへの意義を以下に説明', de: 'Typ 1-9 Regulierte Aktivitätslizenzen + VATP Zusatzbedingungen | Bedeutung jeder Lizenz für RWA unten erklärt', ko: '1-9유형 규제 활동 라이선스 + VATP 추가 조건 | 각 라이선스의 RWA에 대한 의미 아래 설명', ar: 'تراخيص الأنشطة المنظمة من النوع 1-9 + شروط VATP الإضافية | شرح أهمية كل ترخيص لـ RWA أدناه', ru: 'Лицензии на регулируемые виды деятельности типов 1-9 + дополнительные условия VATP | Значение каждой лицензии для RWA объяснено ниже' },
  licenseCanDo: { zh: '持牌可从事的RWA活动：', en: 'RWA activities permitted with this license:', fr: 'Activités RWA autorisées avec cette licence :', es: 'Actividades RWA permitidas con esta licencia:', pt: 'Atividades RWA permitidas com esta licença:', ja: 'このライセンスで許可されるRWA活動：', de: 'Mit dieser Lizenz erlaubte RWA-Aktivitäten:', ko: '이 라이선스로 허용되는 RWA 활동:', ar: 'أنشطة RWA المسموح بها بهذا الترخيص:', ru: 'Деятельность RWA, разрешенная с этой лицензией:' },
  licenseComboTitle: { zh: '完整RWA发行所需牌照组合', en: 'License Combinations Required for Full RWA Issuance', fr: 'Combinaisons de Licences Requises pour l\'Émission RWA Complète', es: 'Combinaciones de Licencias Requeridas para la Emisión RWA Completa', pt: 'Combinações de Licenças Necessárias para Emissão RWA Completa', ja: '完全なRWA発行に必要なライセンスの組み合わせ', de: 'Lizenzkombinationen für vollständige RWA-Ausgabe erforderlich', ko: '완전한 RWA 발행에 필요한 라이선스 조합', ar: 'مجموعات التراخيص المطلوبة لإصدار RWA الكامل', ru: 'Комбинации лицензий, необходимые для полного выпуска RWA' },
  vaspDiffTitle: { zh: '一句话记住区别', en: 'Remember the Difference in One Sentence', fr: 'Retenir la Différence en Une Phrase', es: 'Recordar la Diferencia en Una Oración', pt: 'Lembrar a Diferença em Uma Frase', ja: '一言で違いを覚える', de: 'Den Unterschied in einem Satz merken', ko: '한 문장으로 차이 기억하기', ar: 'تذكر الفرق في جملة واحدة', ru: 'Запомнить разницу в одном предложении' },
  vaspConcept: { zh: 'VASP = 国际通用概念', en: 'VASP = International Universal Concept', fr: 'VASP = Concept Universel International', es: 'VASP = Concepto Universal Internacional', pt: 'VASP = Conceito Universal Internacional', ja: 'VASP = 国際的な汎用概念', de: 'VASP = Internationales Universalkonzept', ko: 'VASP = 국제 범용 개념', ar: 'VASP = مفهوم دولي عالمي', ru: 'VASP = Международная универсальная концепция' },
  vaspConceptDesc: { zh: '就像"汽车"是通用说法，全球监管机构都用VASP泛指加密机构。新加坡/美国/欧盟都叫VASP。', en: 'Like "automobile" is a universal term, regulators worldwide use VASP to refer to crypto institutions. Singapore/US/EU all use VASP.', fr: 'Comme "automobile" est un terme universel, les régulateurs du monde entier utilisent VASP pour désigner les institutions crypto. Singapour/États-Unis/UE utilisent tous VASP.', es: 'Como "automóvil" es un término universal, los reguladores de todo el mundo usan VASP para referirse a las instituciones cripto. Singapur/EE.UU./UE todos usan VASP.', pt: 'Como "automóvel" é um termo universal, reguladores em todo o mundo usam VASP para se referir a instituições cripto. Singapura/EUA/UE todos usam VASP.', ja: '「自動車」が汎用語であるように、世界中の規制当局がVASPを暗号機関の総称として使用しています。シンガポール/米国/EUすべてVASPを使用。', de: 'Wie "Automobil" ein universeller Begriff ist, verwenden Regulatoren weltweit VASP für Krypto-Institutionen. Singapur/USA/EU verwenden alle VASP.', ko: '"자동차"가 범용 용어인 것처럼, 전 세계 규제 기관은 VASP를 암호화폐 기관을 지칭하는 데 사용합니다. 싱가포르/미국/EU 모두 VASP 사용.', ar: 'مثلما أن "السيارة" مصطلح عالمي، يستخدم المنظمون في جميع أنحاء العالم VASP للإشارة إلى مؤسسات التشفير. سنغافورة/الولايات المتحدة/الاتحاد الأوروبي جميعهم يستخدمون VASP.', ru: 'Как "автомобиль" — универсальный термин, регуляторы по всему миру используют VASP для обозначения крипто-учреждений. Сингапур/США/ЕС — все используют VASP.' },
  vatpConcept: { zh: 'VATP = 香港专用标签', en: 'VATP = Hong Kong Exclusive Label', fr: 'VATP = Étiquette Exclusive de Hong Kong', es: 'VATP = Etiqueta Exclusiva de Hong Kong', pt: 'VATP = Rótulo Exclusivo de Hong Kong', ja: 'VATP = 香港専用ラベル', de: 'VATP = Exklusives Hongkong-Label', ko: 'VATP = 홍콩 전용 레이블', ar: 'VATP = تسمية حصرية لهونغ كونغ', ru: 'VATP = Эксклюзивный ярлык Гонконга' },
  vatpConceptDesc: { zh: '就像"港式奶茶"是香港专属，VATP只在香港使用，代表通过SFC严苛审核的交易平台。全球只有12家。', en: 'Like "Hong Kong milk tea" is HK-exclusive, VATP is only used in Hong Kong, representing platforms that passed SFC\'s rigorous review. Only 12 globally.', fr: 'Comme le "thé au lait de Hong Kong" est exclusif à HK, VATP n\'est utilisé qu\'à Hong Kong, représentant les plateformes ayant passé l\'examen rigoureux de la SFC. Seulement 12 dans le monde.', es: 'Como el "té con leche de Hong Kong" es exclusivo de HK, VATP solo se usa en Hong Kong, representando plataformas que pasaron la rigurosa revisión de SFC. Solo 12 en el mundo.', pt: 'Como o "chá com leite de Hong Kong" é exclusivo de HK, VATP é usado apenas em Hong Kong, representando plataformas que passaram pela rigorosa revisão da SFC. Apenas 12 no mundo.', ja: '「香港ミルクティー」がHK専属であるように、VATOは香港でのみ使用され、SFCの厳格な審査を通過したプラットフォームを表します。世界で12社のみ。', de: 'Wie "Hongkonger Milchtee" HK-exklusiv ist, wird VATP nur in Hong Kong verwendet und repräsentiert Plattformen, die SFCs strenge Prüfung bestanden haben. Weltweit nur 12.', ko: '"홍콩 밀크티"가 HK 전용인 것처럼, VATP는 홍콩에서만 사용되며 SFC의 엄격한 심사를 통과한 플랫폼을 나타냅니다. 전 세계 12개뿐.', ar: 'مثلما أن "شاي الحليب في هونغ كونغ" حصري لـ HK، يُستخدم VATP فقط في هونغ كونغ، ويمثل المنصات التي اجتازت مراجعة SFC الصارمة. 12 فقط في العالم.', ru: 'Как "молочный чай по-гонконгски" — эксклюзив HK, VATP используется только в Гонконге, представляя платформы, прошедшие строгую проверку SFC. Только 12 в мире.' },
  checklistTitle: { zh: 'SFC 合规自查清单（扩展版）', en: 'SFC Compliance Checklist (Extended)', fr: 'Liste de Conformité SFC (Étendue)', es: 'Lista de Cumplimiento SFC (Extendida)', pt: 'Lista de Conformidade SFC (Estendida)', ja: 'SFCコンプライアンスチェックリスト（拡張版）', de: 'SFC-Compliance-Checkliste (Erweitert)', ko: 'SFC 컴플라이언스 체크리스트 (확장판)', ar: 'قائمة امتثال SFC (الموسعة)', ru: 'Контрольный список соответствия SFC (расширенный)' },
  checklistDesc: { zh: 'RWA发行方上线前须逐条核查 · 共16条必须项', en: 'RWA issuers must check each item before launch · 16 mandatory items', fr: 'Les émetteurs RWA doivent vérifier chaque élément avant le lancement · 16 éléments obligatoires', es: 'Los emisores de RWA deben verificar cada elemento antes del lanzamiento · 16 elementos obligatorios', pt: 'Os emissores de RWA devem verificar cada item antes do lançamento · 16 itens obrigatórios', ja: 'RWA発行者はローンチ前に各項目を確認する必要があります · 16の必須項目', de: 'RWA-Emittenten müssen jeden Punkt vor dem Start überprüfen · 16 Pflichtpunkte', ko: 'RWA 발행자는 출시 전 각 항목을 확인해야 합니다 · 16개 필수 항목', ar: 'يجب على مصدري RWA التحقق من كل بند قبل الإطلاق · 16 بنداً إلزامياً', ru: 'Эмитенты RWA должны проверить каждый пункт перед запуском · 16 обязательных пунктов' },
  checklistSource: { zh: '来源：SFC《有关证券型代币发行的通函》（2023.11）及《VASP发牌指引》。本清单仅供参考，正式发行前须咨询SFC持牌机构及合规律师。', en: 'Source: SFC Circular on Security Token Offerings (2023.11) and VASP Licensing Guidelines. This checklist is for reference only; consult SFC-licensed institutions and compliance lawyers before formal issuance.', fr: 'Source : Circulaire SFC sur les offres de jetons de sécurité (2023.11) et directives de licence VASP. Cette liste est à titre indicatif uniquement ; consultez des institutions agréées SFC et des avocats en conformité avant l\'émission formelle.', es: 'Fuente: Circular SFC sobre Ofertas de Tokens de Seguridad (2023.11) y Directrices de Licencia VASP. Esta lista es solo de referencia; consulte instituciones con licencia SFC y abogados de cumplimiento antes de la emisión formal.', pt: 'Fonte: Circular SFC sobre Ofertas de Tokens de Segurança (2023.11) e Diretrizes de Licença VASP. Esta lista é apenas para referência; consulte instituições licenciadas pela SFC e advogados de conformidade antes da emissão formal.', ja: '出典：SFC証券型トークン発行に関する通達（2023.11）およびVASPライセンスガイドライン。このチェックリストは参考用のみです。正式発行前にSFC認可機関とコンプライアンス弁護士に相談してください。', de: 'Quelle: SFC-Rundschreiben zu Security Token Offerings (2023.11) und VASP-Lizenzierungsrichtlinien. Diese Checkliste dient nur als Referenz; konsultieren Sie SFC-lizenzierte Institutionen und Compliance-Anwälte vor der formellen Ausgabe.', ko: '출처: SFC 증권형 토큰 공모에 관한 통함(2023.11) 및 VASP 라이선스 지침. 이 체크리스트는 참고용입니다. 공식 발행 전 SFC 인가 기관 및 컴플라이언스 변호사와 상담하세요.', ar: 'المصدر: تعميم SFC حول عروض الرموز الأمنية (2023.11) وإرشادات ترخيص VASP. هذه القائمة للإشارة فقط؛ استشر المؤسسات المرخصة من SFC ومحامي الامتثال قبل الإصدار الرسمي.', ru: 'Источник: Циркуляр SFC о предложениях токенов ценных бумаг (2023.11) и руководящие принципы лицензирования VASP. Этот контрольный список предназначен только для справки; проконсультируйтесь с лицензированными SFC учреждениями и юристами по соответствию перед официальным выпуском.' },
  officialWebsite: { zh: '官网直达', en: 'Official Website', fr: 'Site Officiel', es: 'Sitio Oficial', pt: 'Site Oficial', ja: '公式サイト', de: 'Offizielle Website', ko: '공식 웹사이트', ar: 'الموقع الرسمي', ru: 'Официальный сайт' },
  founded: { zh: '成立年份：', en: 'Founded:', fr: 'Fondé :', es: 'Fundado:', pt: 'Fundado:', ja: '設立年：', de: 'Gegründet:', ko: '설립 연도:', ar: 'تأسست:', ru: 'Основан:' },
  sandboxParticipation: { zh: '沙盒参与：', en: 'Sandbox:', fr: 'Sandbox :', es: 'Sandbox:', pt: 'Sandbox:', ja: 'サンドボックス：', de: 'Sandbox:', ko: '샌드박스:', ar: 'صندوق الرمل:', ru: 'Песочница:' },
  majorEvents: { zh: '重大事件：', en: 'Major Events:', fr: 'Événements Majeurs :', es: 'Eventos Importantes:', pt: 'Eventos Importantes:', ja: '重大イベント：', de: 'Wichtige Ereignisse:', ko: '주요 사건:', ar: 'أحداث رئيسية:', ru: 'Важные события:' },
  rwaCase: { zh: 'RWA案例：', en: 'RWA Cases:', fr: 'Cas RWA :', es: 'Casos RWA:', pt: 'Casos RWA:', ja: 'RWA事例：', de: 'RWA-Fälle:', ko: 'RWA 사례:', ar: 'حالات RWA:', ru: 'Случаи RWA:' },
  sandboxYes: { zh: '是', en: 'Yes', fr: 'Oui', es: 'Sí', pt: 'Sim', ja: 'はい', de: 'Ja', ko: '예', ar: 'نعم', ru: 'Да' },
  sandboxNo: { zh: '否', en: 'No', fr: 'Non', es: 'No', pt: 'Não', ja: 'いいえ', de: 'Nein', ko: '아니오', ar: 'لا', ru: 'Нет' },
  sandboxParticipated: { zh: '沙盒参与', en: 'Sandbox', fr: 'Sandbox', es: 'Sandbox', pt: 'Sandbox', ja: 'サンドボックス', de: 'Sandbox', ko: '샌드박스', ar: 'صندوق الرمل', ru: 'Песочница' },
  beian: { zh: '备案/资质：', en: 'Registration:', fr: 'Enregistrement :', es: 'Registro:', pt: 'Registro:', ja: '登録/資格：', de: 'Registrierung:', ko: '등록/자격:', ar: 'التسجيل:', ru: 'Регистрация:' },
  type: { zh: '类型：', en: 'Type:', fr: 'Type :', es: 'Tipo:', pt: 'Tipo:', ja: 'タイプ：', de: 'Typ:', ko: '유형:', ar: 'النوع:', ru: 'Тип:' },
  iotSupport: { zh: 'IoT支持：', en: 'IoT Support:', fr: 'Support IoT :', es: 'Soporte IoT:', pt: 'Suporte IoT:', ja: 'IoTサポート：', de: 'IoT-Unterstützung:', ko: 'IoT 지원:', ar: 'دعم IoT:', ru: 'Поддержка IoT:' },
  cert: { zh: '认证：', en: 'Certification:', fr: 'Certification :', es: 'Certificación:', pt: 'Certificação:', ja: '認証：', de: 'Zertifizierung:', ko: '인증:', ar: 'الشهادة:', ru: 'Сертификация:' },
  focus: { zh: '聚焦：', en: 'Focus:', fr: 'Focus :', es: 'Enfoque:', pt: 'Foco:', ja: 'フォーカス：', de: 'Fokus:', ko: '집중:', ar: 'التركيز:', ru: 'Фокус:' },
  repCases: { zh: '代表案例', en: 'Representative Cases', fr: 'Cas Représentatifs', es: 'Casos Representativos', pt: 'Casos Representativos', ja: '代表事例', de: 'Repräsentative Fälle', ko: '대표 사례', ar: 'حالات تمثيلية', ru: 'Представительные случаи' },
  globalMilestone: { zh: '全球里程碑', en: 'Global Milestone', fr: 'Jalon Mondial', es: 'Hito Global', pt: 'Marco Global', ja: 'グローバルマイルストーン', de: 'Globaler Meilenstein', ko: '글로벌 이정표', ar: 'معلم عالمي', ru: 'Глобальный рубеж' },
  keyMilestone: { zh: '关键节点', en: 'Key Milestone', fr: 'Jalon Clé', es: 'Hito Clave', pt: 'Marco Chave', ja: '重要マイルストーン', de: 'Schlüsselmeilenstein', ko: '핵심 이정표', ar: 'معلم رئيسي', ru: 'Ключевой рубеж' },
  enterSection: { zh: '进入板块', en: 'Enter Section', fr: 'Entrer dans la Section', es: 'Entrar en la Sección', pt: 'Entrar na Seção', ja: 'セクションに入る', de: 'Abschnitt betreten', ko: '섹션 입장', ar: 'الدخول إلى القسم', ru: 'Войти в раздел' },
};

function getL(key: string, lang: string): string {
  const map = LABELS[key];
  if (!map) return key;
  return (map as Record<string, string>)[lang] ?? (map as Record<string, string>)['en'] ?? key;
}

// ─── Virtual Institution List Component ────────────────────────────────────────
interface VirtualInstitutionListProps {
  data: typeof hkInstitutions;
  lang: string;
  getL: (key: string, lang: string) => string;
}

function VirtualInstitutionList({ data, lang, getL }: VirtualInstitutionListProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();
  const [expandedInst, setExpandedInst] = useState<string | null>(null);
  
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 140, // Collapsed card height
    overscan: 3,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative">
      {isScrollable && (
        <div className="mb-2 flex items-center gap-3">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #7C3AED, #F59E0B)' }}
            />
          </div>
          <span className="text-gray-400 text-xs whitespace-nowrap shrink-0">
            {isAtBottom ? (lang === 'zh' ? '已浏览全部' : 'All viewed') : `${progress}%`}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative h-[600px] overflow-auto"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualItem) => {
              const inst = data[virtualItem.index];
              const expanded = expandedInst === inst.name;
              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  className="p-2"
                >
                  <InstitutionCard 
                    inst={inst} 
                    expanded={expanded} 
                    onToggle={() => setExpandedInst(expanded ? null : inst.name)} 
                    lang={lang} 
                    getL={getL} 
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isScrollable && !isAtBottom && (
        <div className="relative -mt-10 h-10 pointer-events-none rounded-b-2xl overflow-hidden" style={{ background: 'linear-gradient(to bottom, transparent, rgba(250,250,250,0.95))' }}>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-gray-400 text-xs">
            <i className="ri-arrow-down-s-line animate-bounce text-sm"></i>
            <span>{lang === 'zh' ? '滚动查看更多' : 'Scroll for more'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Virtual Domestic Provider List ────────────────────────────────────────────
interface VirtualDomesticListProps {
  data: typeof domesticProviders;
  lang: string;
  getL: (key: string, lang: string) => string;
}

function VirtualDomesticList({ data, lang, getL }: VirtualDomesticListProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();
  
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 180,
    overscan: 2,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative">
      {isScrollable && (
        <div className="mb-2 flex items-center gap-3">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #7C3AED, #10B981)' }}
            />
          </div>
          <span className="text-gray-400 text-xs whitespace-nowrap shrink-0">
            {isAtBottom ? (lang === 'zh' ? '已浏览全部' : 'All viewed') : `${progress}%`}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative h-[600px] overflow-auto space-y-4"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualItem) => {
              const p = data[virtualItem.index];
              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  className="py-2"
                >
                  <div className="bg-[#F5F3FF] border border-[#7C3AED]/10 rounded-2xl p-6 hover:border-[#7C3AED]/30 transition-all">
                    <div className="grid md:grid-cols-4 gap-4 items-start">
                      <div className="md:col-span-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-8 h-8 flex items-center justify-center bg-[#7C3AED] rounded-lg">
                            <i className="ri-database-2-line text-white text-sm"></i>
                          </div>
                          <h4 className="font-black text-gray-800 text-sm">{p.name}</h4>
                        </div>
                        <div className="text-xs text-[#7C3AED] font-medium">{p.group}</div>
                        <div className="text-xs text-gray-500 mt-1">{getL('beian', lang)}{p.beian}</div>
                        <a href={p.url} target="_blank" rel="nofollow noreferrer" className="text-xs text-[#7C3AED] hover:underline mt-1 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <i className="ri-external-link-line text-xs"></i>{getL('officialWebsite', lang)}
                        </a>
                      </div>
                      <div className="md:col-span-2">
                        <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                          <div><span className="text-gray-400">{getL('type', lang)}</span><span className="text-gray-700">{p.type}</span></div>
                          <div><span className="text-gray-400">{getL('iotSupport', lang)}</span><span className="text-gray-700">{p.iot}</span></div>
                          <div><span className="text-gray-400">{getL('cert', lang)}</span><span className="text-green-700 font-medium">{p.cert}</span></div>
                          <div><span className="text-gray-400">{getL('focus', lang)}</span><span className="text-gray-700">{p.focus}</span></div>
                        </div>
                        <p className="text-xs text-gray-600 leading-relaxed">{p.note}</p>
                      </div>
                      <div className="md:col-span-1">
                        <div className="text-xs font-bold text-gray-700 mb-1">{getL('repCases', lang)}</div>
                        <p className="text-xs text-gray-500 leading-relaxed">{p.cases}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isScrollable && !isAtBottom && (
        <div className="relative -mt-10 h-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(245,243,255,0.95))' }}>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-gray-400 text-xs">
            <i className="ri-arrow-down-s-line animate-bounce text-sm"></i>
            <span>{lang === 'zh' ? '滚动查看更多' : 'Scroll for more'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Virtual License List ──────────────────────────────────────────────────────
interface VirtualLicenseListProps {
  data: typeof licenseSystem;
  lang: string;
  getL: (key: string, lang: string) => string;
}

function VirtualLicenseList({ data, lang, getL }: VirtualLicenseListProps) {
  const { containerRef, progress, isScrollable, isAtBottom } = useScrollProgress();
  
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 200,
    overscan: 2,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="relative">
      {isScrollable && (
        <div className="mb-2 flex items-center gap-3">
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-150"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #7C3AED, #EF4444)' }}
            />
          </div>
          <span className="text-gray-400 text-xs whitespace-nowrap shrink-0">
            {isAtBottom ? (lang === 'zh' ? '已浏览全部' : 'All viewed') : `${progress}% · ${lang === 'zh' ? '共10类牌照' : '10 license types'}`}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        className="relative h-[600px] overflow-auto"
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="relative" style={{ height: `${virtualizer.getTotalSize()}px` }}>
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map((virtualItem) => {
              const lic = data[virtualItem.index];
              return (
                <div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={virtualizer.measureElement}
                  className="p-2"
                >
                  <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                    <div className="flex items-center gap-3 px-5 py-3" style={{ background: `${lic.color}10`, borderBottom: `2px solid ${lic.color}20` }}>
                      <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: lic.color }}>
                        <i className={`${lic.icon} text-white text-sm`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="font-black text-gray-900 text-sm">{lic.name}：{lic.fullName}</div>
                        <div className="text-xs text-gray-500">{lic.scope}</div>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full font-bold text-white whitespace-nowrap" style={{ background: lic.color }}>
                        RWA {lic.rwaRelevance}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-gray-700 leading-relaxed mb-3">{lic.desc}</p>
                      <div className="text-xs font-bold text-gray-600 mb-1.5">{getL('licenseCanDo', lang)}</div>
                      <div className="flex flex-wrap gap-1">
                        {lic.canDo.map((item) => (
                          <span key={item} className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${lic.color}10`, color: lic.color }}>
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isScrollable && !isAtBottom && (
        <div className="relative -mt-10 h-10 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.95))' }}>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex items-center gap-1 text-gray-400 text-xs">
            <i className="ri-arrow-down-s-line animate-bounce text-sm"></i>
            <span>{lang === 'zh' ? '滚动查看更多' : 'Scroll for more'}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HKInstitutions() {
  const { lang } = useLang();
  const [tab, setTab] = useState<'hk' | 'domestic' | 'checklist' | 'license' | 'vaspdiff'>('hk');

  const sRanks = hkInstitutions.filter((i) => i.rank === 'S');
  const aRanks = hkInstitutions.filter((i) => i.rank === 'A');
  const bRanks = hkInstitutions.filter((i) => i.rank === 'B');

  return (
    <>
      <section id="hk-institutions" className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/10 rounded-full mb-4">
              <i className="ri-building-2-line text-[#7C3AED] text-sm"></i>
              <span className="text-[#7C3AED] text-sm font-semibold">{getL('sectionBadge', lang)}</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">{getL('sectionTitle', lang)}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base">
              {getL('sectionDesc', lang)}
            </p>
          </div>

          {/* Tab */}
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {[
              { id: 'hk', labelKey: 'tabHK' },
              { id: 'domestic', labelKey: 'tabDomestic' },
              { id: 'license', labelKey: 'tabLicense' },
              { id: 'vaspdiff', labelKey: 'tabVasp' },
              { id: 'checklist', labelKey: 'tabChecklist' },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id as typeof tab)}
                className={`px-4 py-2 rounded-full text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  tab === t.id
                    ? 'bg-[#7C3AED] text-white shadow-md'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-[#7C3AED] hover:text-[#7C3AED]'
                }`}
              >
                {getL(t.labelKey, lang)}
              </button>
            ))}
          </div>

          {/* HK Institutions with Virtual Scrolling */}
          {tab === 'hk' && (
            <div className="space-y-6">
              {sRanks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#7C3AED] text-white text-xs font-black rounded-full">{getL('sRankLabel', lang)}</span>
                    <span className="text-gray-400 text-sm">{getL('sRankDesc', lang)}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {sRanks.map((inst) => (
                      <InstitutionCard key={inst.name} inst={inst} expanded={false} onToggle={() => {}} lang={lang} getL={getL} />
                    ))}
                  </div>
                </div>
              )}
              
              {aRanks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-[#F59E0B] text-white text-xs font-black rounded-full">{getL('aRankLabel', lang)}</span>
                    <span className="text-gray-400 text-sm">{getL('aRankDesc', lang)}</span>
                  </div>
                  <VirtualInstitutionList data={aRanks} lang={lang} getL={getL} />
                </div>
              )}
              
              {bRanks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <span className="px-3 py-1 bg-gray-400 text-white text-xs font-black rounded-full">{getL('bRankLabel', lang)}</span>
                    <span className="text-gray-400 text-sm">{getL('bRankDesc', lang)}</span>
                  </div>
                  <VirtualInstitutionList data={bRanks} lang={lang} getL={getL} />
                </div>
              )}
              
              <div className="bg-[#F5F3FF] border border-[#7C3AED]/15 rounded-xl p-4 text-sm text-[#6D28D9]">
                <strong>{lang === 'zh' ? '数据来源：' : 'Data source: '}</strong>{getL('dataSource', lang)}
              </div>

              {/* Institution Compare */}
              <InstitutionCompare institutions={hkInstitutions} lang={lang} />
            </div>
          )}

          {/* Domestic with Virtual Scrolling */}
          {tab === 'domestic' && (
            <div id="domestic" className="space-y-4">
              <VirtualDomesticList data={domesticProviders} lang={lang} getL={getL} />
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
                <i className="ri-information-line text-amber-600 text-xl shrink-0 mt-0.5"></i>
                <p className="text-amber-700 text-sm leading-relaxed">
                  <strong>{lang === 'zh' ? '重要提示：' : 'Important: '}</strong>{getL('domesticWarning', lang)}
                </p>
              </div>
            </div>
          )}

          {/* License System with Virtual Scrolling */}
          {tab === 'license' && (
            <div className="space-y-4">
              <div className="bg-[#7C3AED]/5 border border-[#7C3AED]/20 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#7C3AED] rounded-xl"><i className="ri-shield-star-line text-white text-base"></i></div>
                  <div>
                    <div className="font-black text-gray-900">{getL('licenseSystemTitle', lang)}</div>
                    <div className="text-xs text-gray-500">{getL('licenseSystemDesc', lang)}</div>
                  </div>
                </div>
              </div>
              
              <VirtualLicenseList data={licenseSystem} lang={lang} getL={getL} />
              
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-2">
                <div className="font-black text-amber-800 mb-2 flex items-center gap-2">
                  <i className="ri-lightbulb-flash-line text-amber-500"></i>
                  {getL('licenseComboTitle', lang)}
                </div>
                <div className="grid md:grid-cols-3 gap-3 text-xs">
                  {[
                    { title: lang === 'zh' ? '基础RWA发行' : 'Basic RWA Issuance', combo: '1类（分销）+ 9类（管理）', note: lang === 'zh' ? '最低合规要求，适合标准代币化发行' : 'Minimum compliance, suitable for standard tokenized issuance' },
                    { title: lang === 'zh' ? '完整RWA平台' : 'Full RWA Platform', combo: '1类+4类+7类+9类', note: lang === 'zh' ? 'VATP级别，可提供一站式发行+建议+交易+管理' : 'VATP level, one-stop issuance + advisory + trading + management' },
                    { title: lang === 'zh' ? '机构RWA发行' : 'Institutional RWA Issuance', combo: '1类+4类+6类+9类', note: lang === 'zh' ? '适合提供完整融资顾问服务的投行级服务' : 'Suitable for investment bank-level full financing advisory services' },
                  ].map((item) => (
                    <div key={item.title} className="bg-white rounded-xl p-3 border border-amber-100">
                      <div className="font-bold text-amber-800 mb-1">{item.title}</div>
                      <div className="text-amber-700 font-mono font-medium mb-1">{item.combo}</div>
                      <div className="text-amber-600">{item.note}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* VASP vs VATP */}
          {tab === 'vaspdiff' && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {[vaspVvatpDiff.vasp, vaspVvatpDiff.vatp].map((item) => (
                  <div key={item.title} className="bg-white border-2 rounded-2xl overflow-hidden" style={{ borderColor: item.color }}>
                    <div className="px-5 py-4 text-white font-black text-lg" style={{ background: item.color }}>
                      {item.title}
                      <div className="text-sm font-normal opacity-80 mt-0.5">{item.full}</div>
                    </div>
                    <div className="p-5 space-y-3 text-sm">
                      {[
                        { label: lang === 'zh' ? '覆盖范围' : 'Scope', value: item.scope, icon: 'ri-global-line' },
                        { label: lang === 'zh' ? '香港用法' : 'HK Usage', value: item.hk, icon: 'ri-flag-2-line' },
                        { label: lang === 'zh' ? '全球使用' : 'Global Use', value: item.global, icon: 'ri-earth-line' },
                        { label: lang === 'zh' ? '谁需要' : 'Who Needs It', value: item.who, icon: 'ri-group-line' },
                        { label: lang === 'zh' ? '监管机构' : 'Regulator', value: item.regulate, icon: 'ri-shield-check-line' },
                      ].map((row) => (
                        <div key={row.label} className="flex gap-3">
                          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                            <i className={`${row.icon} text-xs`} style={{ color: item.color }}></i>
                          </div>
                          <div>
                            <span className="font-bold text-gray-700">{row.label}：</span>
                            <span className="text-gray-600">{row.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5">
                <div className="font-black text-gray-800 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#7C3AED]"></i>
                  {getL('vaspDiffTitle', lang)}
                </div>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="font-bold text-gray-700 mb-1">{getL('vaspConcept', lang)}</div>
                    <p className="text-gray-600">{getL('vaspConceptDesc', lang)}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-100">
                    <div className="font-bold text-[#7C3AED] mb-1">{getL('vatpConcept', lang)}</div>
                    <p className="text-gray-600">{getL('vatpConceptDesc', lang)}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Checklist */}
          {tab === 'checklist' && (
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl p-8" style={{ background: 'linear-gradient(135deg, #F5F3FF, #EDE9FE)' }}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#7C3AED] rounded-xl">
                    <i className="ri-shield-check-fill text-white text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="font-black text-gray-800 text-lg">{getL('checklistTitle', lang)}</h3>
                    <p className="text-gray-500 text-sm">{getL('checklistDesc', lang)}</p>
                  </div>
                </div>
                <div className="space-y-2.5">
                  {selfCheckList.map((item, i) => (
                    <div key={i} className={`flex items-start gap-3 p-3 rounded-xl ${i === 0 ? 'bg-[#7C3AED] text-white' : 'bg-white'}`}>
                      <div className={`w-6 h-6 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${i === 0 ? 'bg-white/20' : 'bg-[#7C3AED]/10'}`}>
                        {i === 0 ? (
                          <i className="ri-checkbox-circle-fill text-white text-sm"></i>
                        ) : (
                          <span className="text-[#7C3AED] text-xs font-bold">{i}</span>
                        )}
                      </div>
                      <span className={`text-sm leading-relaxed ${i === 0 ? 'text-white font-bold' : 'text-gray-700'}`}>{item}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-4 border-t border-[#7C3AED]/20 text-xs text-[#6D28D9]">
                  {getL('checklistSource', lang)}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function InstitutionCard({
  inst,
  expanded,
  onToggle,
  lang,
  getL,
}: {
  inst: typeof hkInstitutions[0];
  expanded: boolean;
  onToggle: () => void;
  lang: string;
  getL: (key: string, lang: string) => string;
}) {
  return (
    <div
      className="bg-[#FAFAFA] border border-gray-100 rounded-2xl overflow-hidden cursor-pointer hover:border-[#7C3AED]/30 hover:-translate-y-0.5 transition-all"
      onClick={onToggle}
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-black text-gray-800 text-sm">{inst.name}</h4>
            <div className="text-xs text-gray-500 mt-0.5">{inst.license}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              inst.rank === 'S' ? 'bg-[#7C3AED] text-white' : inst.rank === 'A' ? 'bg-[#F59E0B] text-white' : 'bg-gray-200 text-gray-600'
            }`}>
              {inst.rank} {lang === 'zh' ? '级' : 'Class'}
            </span>
            <i className={`text-gray-400 text-sm ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </div>
        </div>
        <div className="text-xs text-gray-500 mb-2 leading-relaxed">{inst.rwa}</div>
        <div className="text-xs text-gray-400 italic line-clamp-2">{inst.note}</div>
      </div>
      
      {expanded && (
        <div className="border-t border-gray-100 px-5 pb-4 pt-3 space-y-2.5 bg-white">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div><span className="text-gray-400">{getL('founded', lang)}</span><span className="text-gray-700">{inst.founded}</span></div>
            <div><span className="text-gray-400">{getL('sandboxParticipation', lang)}</span><span className="text-gray-700">{inst.sandbox}</span></div>
          </div>
          <div className="text-xs">
            <span className="font-bold text-gray-700">{getL('majorEvents', lang)}</span>
            <span className="text-gray-600 ml-1">{inst.bigEvents}</span>
          </div>
          <div className="text-xs">
            <span className="font-bold text-gray-700">{getL('rwaCase', lang)}</span>
            <span className="text-gray-600 ml-1">{inst.rwaCase}</span>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-gray-100">
            <a href={inst.url} target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-1 text-xs text-[#7C3AED] hover:underline font-medium cursor-pointer" onClick={(e) => e.stopPropagation()}>
              <i className="ri-external-link-line text-xs"></i>{getL('officialWebsite', lang)}
            </a>
            <div className="flex items-center gap-1">
              {inst.sandbox === '是' && <span className="text-xs px-1.5 py-0.5 bg-green-50 text-green-700 rounded-full">{getL('sandboxParticipated', lang)}</span>}
              <span className="text-xs px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded-full">{inst.type}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}